import React, { useContext, useMemo } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import BrandLoader from "../../components/shared/BrandLoader/BrandLoader";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../hooks/userContext";
import { PharmacyContext } from "../../hooks/pharmacyContext";
import { useQueryUniqueUsers } from "../../queries/pharmacy/hooks/useQueryUniqueUsers";
import { useQueryUniquePharmacy } from "../../queries/pharmacy/hooks/useQueryUniquePharmacy";
import { useQueryPharmacyOptions } from "../../queries/pharmacy/hooks/useQueryPharmacyOptions";
import PharmacyFiles from "../PharmacyProfile/PharmacyFiles";
import ReviewFollowingStatements from "../PharmacyProfile/ReviewFollowingStatements";
import { routeConfigs } from "../../navigation/routeConfig";

const INK = "#1E293B";
const INK_SOFT = "#475569";
const PRIMARY = "#2563EB";
const LINE = "#E2E8F0";
const BODY_FONT = '"Source Sans 3", sans-serif';
const DISPLAY_FONT = '"Lexend", sans-serif';

export const ComplianceVerification: React.FC = () => {
  const navigate = useNavigate();
  const { id }: any = useContext(UserContext);
  const pharmacyUser: any = useContext(PharmacyContext);
  const pharmacyFromContext = pharmacyUser?.Pharmacy;

  const { data: pharmacyUsersData, loading: pharmacyUsersLoading } = useQueryUniqueUsers({
    variables: { where: { id: +id } },
  });

  const pharmacyId =
    pharmacyUsersData?.findUniqueUser?.PharmacyUsers?.[0]?.pharmacyId ?? pharmacyUser?.pharmacyId;

  const { data: pharmacyOptionData, loading: pharmacyOptionsLoading } = useQueryPharmacyOptions({});
  const { data: pharmacyData, loading: pharmacyLoading } = useQueryUniquePharmacy({
    variables: { where: { id: pharmacyId } },
    skip: !pharmacyId,
  });

  const pharmacy = pharmacyData?.findUniquePharmacy || pharmacyFromContext;

  const bankAccounts = pharmacy?.BankAccount || pharmacyFromContext?.BankAccount || [];
  const bankingVerified = bankAccounts.some(
    (item: any) =>
      (item.object === "payment_method" || item.object === "card") &&
      (item.status === "VERIFIED" || item.status === "APPROVED")
  );

  const statusKey =
    pharmacy?.PharmacyStatus?.Status?.key ||
    pharmacyFromContext?.PharmacyStatus?.Status?.key ||
    "";
  const statusName =
    pharmacy?.PharmacyStatus?.Status?.name ||
    pharmacyFromContext?.PharmacyStatus?.Status?.name ||
    statusKey ||
    "Unknown";
  const pharmacyVerified = statusKey === "APPROVED";

  const rows = useMemo(
    () => [
      {
        label: "Pharmacy verification",
        ok: pharmacyVerified,
        detail: statusName,
        actionLabel: "Update profile",
        path: routeConfigs.pharmacyProfile.path,
      },
      {
        label: "Banking / payouts",
        ok: bankingVerified,
        detail: bankingVerified ? "Payment method verified" : "Verification required",
        actionLabel: "Update banking",
        path: routeConfigs.bankAccount.path,
      },
      {
        label: "DSCSA marketplace access",
        ok: pharmacyVerified,
        detail: pharmacyVerified
          ? "Eligible to buy and sell on the marketplace"
          : "Complete pharmacy verification to unlock full access",
        actionLabel: "Review profile",
        path: routeConfigs.pharmacyProfile.path,
      },
    ],
    [pharmacyVerified, statusName, bankingVerified]
  );

  if (pharmacyUsersLoading || pharmacyLoading || pharmacyOptionsLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}>
        <BrandLoader size={48} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "12px",
          border: `1px solid ${LINE}`,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Typography
          sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: 18, color: INK, mb: 0.5 }}
        >
          Compliance & Verification
        </Typography>
        <Typography sx={{ fontFamily: BODY_FONT, fontSize: 13, color: INK_SOFT, mb: 2 }}>
          Track verification status and required compliance documentation
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {rows.map((row) => (
            <Box
              key={row.label}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.25,
                p: 1.5,
                borderRadius: "8px",
                bgcolor: row.ok ? "rgba(46, 125, 50, 0.06)" : "rgba(230, 81, 0, 0.06)",
              }}
            >
              {row.ok ? (
                <CheckCircleOutlineIcon sx={{ fontSize: 20, color: "#2E7D32", mt: 0.15 }} />
              ) : (
                <ErrorOutlineIcon sx={{ fontSize: 20, color: "#E65100", mt: 0.15 }} />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: BODY_FONT, fontWeight: 600, fontSize: 14, color: INK }}>
                  {row.label}
                </Typography>
                <Typography sx={{ fontFamily: BODY_FONT, fontSize: 13, color: INK_SOFT }}>
                  {row.detail}
                </Typography>
              </Box>
              {!row.ok && (
                <Button
                  size="small"
                  onClick={() => navigate(row.path)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    color: PRIMARY,
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.actionLabel}
                </Button>
              )}
            </Box>
          ))}
        </Box>
      </Paper>

      {pharmacyData?.findUniquePharmacy && pharmacyOptionData?.findManyStatus && (
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
          <PharmacyFiles
            pharmacy={pharmacyData.findUniquePharmacy}
            statuses={pharmacyOptionData.findManyStatus}
          />
        </Paper>
      )}

      {pharmacyData?.findUniquePharmacy?.PharmacyStatement && (
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
          <ReviewFollowingStatements data={pharmacyData.findUniquePharmacy.PharmacyStatement} />
        </Paper>
      )}
    </Box>
  );
};

export default ComplianceVerification;
